/**
 * readinessAppSections.js — Full-application section definitions for the
 * Readiness experience. Sections are program-scoped: the borrower only ever
 * sees ONE program-specific section, and never a field the selected program
 * doesn't need.
 */

import { numFrom } from './readinessQuestions';

/** Map a selected program display name to a program key. */
export function programKey(selectedProgram) {
  const p = selectedProgram || '';
  return p.includes('VA') ? 'va'
    : p.includes('USDA') ? 'usda'
    : p.includes('FHA') ? 'fha'
    : p.includes('Jumbo') ? 'jumbo'
    : p.includes('Bank') ? 'bank'
    : 'conv';
}

const sel = (opts) => [{ v: '', l: 'Select\u2026' }].concat(opts.map((o) => ({ v: o, l: o })));
const yn = sel(['Yes', 'No']);

/**
 * Loan-to-value from assessment answers, or null when unknown.
 * Purchase: (value − down) / value. Refinance: `down` holds the payoff.
 */
export function ltvFromAnswers(answers = {}) {
  const value = numFrom(answers.value);
  if (!value || answers.down === undefined) return null;
  const down = numFrom(answers.down);
  const loan = answers.purpose === 'refinance' ? down : value - down;
  if (loan <= 0) return null;
  return Math.round((loan / value) * 100);
}

const MI_EXPLAIN = {
  title: 'Mortgage insurance, explained',
  body: "Mortgage insurance (PMI on conventional loans) protects the lender — not you — if a loan isn't repaid. It's required when your loan is more than 80% of the home's value (less than 20% equity), and not required at or below 80%. If you do have it, expect roughly 0.3–1.5% of the loan amount per year, added to your monthly payment — your credit score and equity set the exact price. You can request removal once you reach 20% equity, and it drops off automatically at 22%.",
};

/**
 * Build the ordered section list for the full application.
 * @param {Object} answers — assessment answers (employment drives section 2)
 * @param {string|null} selectedProgram — display name of the chosen program
 */
export function buildAppSections(answers = {}, selectedProgram = null) {
  const prog = selectedProgram || 'your loan';
  const key = programKey(selectedProgram);
  const secs = [];

  secs.push({
    id: 'aboutYou', title: 'About you',
    intro: "First section: the basics about you. A few fields at once — fill in what you're comfortable with, skip anything.",
    why: { title: 'Identity anchors your file', body: 'Every ' + prog + ' file starts with who is borrowing. Nothing is shared with anyone until you choose to submit.' },
    fields: [
      { k: 'first', label: 'First name', type: 'text', ph: 'Alex' },
      { k: 'last', label: 'Last name', type: 'text', ph: 'Rivera' },
      { k: 'phone', label: 'Mobile phone', type: 'text', ph: '(555) 555-0100' },
      { k: 'marital', label: 'Marital status', type: 'select', options: sel(['Married', 'Unmarried', 'Separated']) },
    ],
  });

  const emp = answers.employment;
  secs.push({
    id: 'employment', title: 'Income & work',
    intro: "Now income — the heart of any application. Remember, I'm an AI: no person sees these numbers.",
    why: key === 'bank'
      ? { title: 'Bank statements do the work', body: 'For a ' + prog + ' loan, deposits on your statements stand in for tax returns — these details frame that analysis.' }
      : { title: 'Income sets your ceiling', body: 'Steady, documentable income is what a ' + prog + ' underwriter weighs most — these fields frame that story.' },
    fields: emp === 'self' ? [
      { k: 'bizName', label: 'Business name', type: 'text', ph: 'Rivera Design LLC' },
      { k: 'bizYears', label: 'Years in business', type: 'text', ph: '4' },
      { k: 'ownPct', label: 'Ownership %', type: 'text', ph: '100' },
      { k: 'moIncome', label: 'Avg. monthly income', type: 'currency', ph: '9,000' },
    ] : emp === 'retired' ? [
      { k: 'source', label: 'Primary income source', type: 'select', options: sel(['Social Security', 'Pension', 'Investments', 'Other']) },
      { k: 'moIncome', label: 'Monthly income', type: 'currency', ph: '6,500' },
      { k: 'yearsRec', label: 'Years receiving it', type: 'text', ph: '3' },
    ] : [
      { k: 'employer', label: 'Employer', type: 'text', ph: 'Acme Corp' },
      { k: 'title', label: 'Job title', type: 'text', ph: 'Project Manager' },
      { k: 'years', label: 'Years there', type: 'text', ph: '3' },
      { k: 'moIncome', label: 'Monthly gross income', type: 'currency', ph: '9,000' },
    ],
  });

  if (key === 'va') secs.push({
    id: 'program', title: 'Your VA benefit',
    intro: "Now the section that makes your loan special — your VA benefit.",
    why: { title: 'Funding fee & COE', body: 'A 10%+ disability rating can waive the VA funding fee entirely, and first use vs. reuse changes the fee. We can pull your COE electronically later.' },
    fields: [
      { k: 'branch', label: 'Branch of service', type: 'select', options: sel(['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force', 'Guard / Reserves']) },
      { k: 'served', label: 'Years of service', type: 'text', ph: '2012\u20132018' },
      { k: 'disability', label: 'VA disability rating', type: 'select', options: sel(['None', '10\u201340%', '50%+']),
        explain: { title: 'Why the rating matters', body: 'A VA disability rating of 10% or higher waives the VA funding fee entirely — that\u2019s often thousands of dollars kept in your pocket. It never hurts your approval; it only helps your pricing.' } },
      { k: 'usedBefore', label: 'Used a VA loan before?', type: 'select', options: yn,
        explain: { title: 'First use vs. reuse', body: 'The VA funding fee is lower the first time you use the benefit (about 2.15% with $0 down) and higher on reuse (about 3.3%). Your entitlement can be restored and reused — having used it before never disqualifies you.' } },
    ],
  });
  if (key === 'fha') secs.push({
    id: 'program', title: 'FHA specifics',
    intro: "A few FHA-only items — this program has some friendly flexibilities worth capturing.",
    why: { title: 'FHA flexibilities', body: 'FHA allows gift funds for the entire down payment and up to 6% seller concessions — these answers put them to work for you.' },
    fields: [
      { k: 'gift', label: 'Using gift funds?', type: 'select', options: yn,
        explain: { title: 'Gift funds, explained', body: 'FHA lets a family member (and some others) gift your entire down payment — you just need a short gift letter saying it isn\u2019t a loan, plus a paper trail of the transfer. It\u2019s very common and completely allowed.' } },
      { k: 'concessions', label: 'Expecting seller concessions?', type: 'select', options: sel(['Yes', 'No', 'Not sure']),
        explain: { title: 'Seller concessions, explained', body: 'Concessions are closing costs the seller agrees to pay on your behalf — FHA allows up to 6% of the price. They reduce the cash you need at closing and are negotiated as part of your offer.' } },
      { k: 'counseling', label: 'Homebuyer counseling', type: 'select', options: sel(['Completed', 'Planning to', 'Not yet']),
        explain: { title: 'Why counseling comes up', body: 'A HUD-approved homebuyer course (often a few hours, online) is optional for most FHA loans but unlocks certain down-payment assistance programs and can strengthen your file. \u201CNot yet\u201D is a perfectly fine answer.' } },
    ],
  });
  if (key === 'usda') secs.push({
    id: 'program', title: 'USDA household',
    intro: "USDA looks at your whole household, not just you — quick section on that.",
    why: { title: 'Limits are household-wide', body: 'USDA income limits count everyone living in the home, even people not on the loan — that\u2019s why we ask about the household.' },
    fields: [
      { k: 'hhSize', label: 'Household size', type: 'select', options: sel(['1', '2', '3', '4', '5', '6', '7', '8+']) },
      { k: 'hhIncome', label: 'Total household income (annual)', type: 'currency', ph: '95,000',
        explain: { title: 'Why household income?', body: 'USDA is unique: its income limit counts everyone living in the home — even people not on the loan. Limits vary by county and household size, and many households comfortably qualify, so an honest total here is all we need.' } },
      { k: 'otherEarners', label: 'Other earners in the home?', type: 'select', options: yn },
    ],
  });
  if (key === 'jumbo') secs.push({
    id: 'program', title: 'Jumbo strength',
    intro: "Jumbo lenders care about depth — a couple of questions on financial strength.",
    why: { title: 'Depth wins jumbo pricing', body: 'Reserves and a clean real-estate picture are what move jumbo pricing — strong answers here directly lower your rate.' },
    fields: [
      { k: 'reserves', label: 'Liquid reserves after closing', type: 'currency', ph: '120,000',
        explain: { title: 'Reserves, explained', body: 'Reserves are the cash and near-cash (savings, brokerage, most retirement funds) you\u2019d still have after closing, measured in months of your new payment. Jumbo lenders typically like 6\u201312 months — stronger reserves directly improve jumbo pricing.' } },
      { k: 'reo', label: 'Other properties owned', type: 'select', options: sel(['None', '1', '2+']) },
    ],
  });
  if (key === 'bank') secs.push({
    id: 'program', title: 'Bank statement details',
    intro: "The bank-statement specifics — this replaces the tax-return pile entirely.",
    why: { title: 'Statements, not returns', body: 'Your deposits tell the income story. 24 months usually earns better pricing than 12; a CPA letter helps but is optional.' },
    fields: [
      { k: 'stmtType', label: 'Statements you can provide', type: 'select', options: sel(['Personal \u2014 12 mo', 'Personal \u2014 24 mo', 'Business \u2014 12 mo', 'Business \u2014 24 mo']),
        explain: { title: 'How statement qualifying works', body: 'Instead of tax returns, the lender averages the deposits on your bank statements to establish income. 24 months usually earns better pricing than 12, and business statements typically apply an expense factor to deposits.' } },
      { k: 'cpa', label: 'CPA or tax preparer available?', type: 'select', options: yn,
        explain: { title: 'Why we ask about a CPA', body: 'A short letter from a CPA or tax preparer confirming your business and ownership percentage strengthens a bank-statement file. Helpful, but optional — not having one doesn\u2019t stop the loan.' } },
    ],
  });
  if (key === 'conv') {
    const ltv = ltvFromAnswers(answers);
    const noMI = ltv !== null && ltv <= 80;
    const isRefi = answers.purpose === 'refinance';
    const miField = noMI
      ? {
          k: 'pmiPlan', label: 'Mortgage insurance', type: 'note',
          note: `Good news — ${isRefi ? 'with your equity, ' : ''}your loan-to-value is about ${ltv}%, so mortgage insurance isn't required on this loan. Nothing to decide here.`,
          explain: MI_EXPLAIN,
        }
      : {
          k: 'pmiPlan', label: 'Mortgage insurance preference', type: 'select',
          options: sel(['Monthly PMI is fine', 'Prefer lender-paid', 'Avoiding PMI with 20% down']),
          explain: MI_EXPLAIN,
        };
    secs.push({
      id: 'program', title: 'Fine-tuning your conventional loan',
      intro: noMI
        ? "A couple of small items that fine-tune conventional pricing — including some good news about mortgage insurance."
        : "A couple of preferences that fine-tune conventional pricing — no wrong answers.",
      why: noMI
        ? { title: 'Small levers, real money', body: 'Your equity already puts you below the 80% LTV line where mortgage insurance applies, so escrow choice is the main lever left on pricing.' }
        : { title: 'Small levers, real money', body: 'PMI structure and escrow choice both shift your monthly payment and rate on a conventional loan — your preference guides the offers.' },
      fields: [
        miField,
        {
          k: 'escrow', label: 'Taxes & insurance', type: 'select',
          options: sel(['Include in payment (escrow)', 'I\u2019ll pay them myself']),
          explain: { title: 'Escrow, explained', body: "An escrow account folds property taxes and homeowners insurance into your monthly payment — the lender sets the money aside and pays those bills when they're due. It's the default for most borrowers and removes two big annual bills. Paying them yourself is usually allowed at or below 80% LTV, sometimes for a small pricing adjustment." },
        },
      ],
    });
  }

  return secs;
}
